<template>
  <div>
    <v-speed-dial v-model="fab" direction="left" bottom right fixed>
      <template v-slot:activator>
        <v-btn v-model="fab" large color="accent" dark fab>
          <v-icon v-if="fab">mdi-close</v-icon>
          <v-icon v-else>mdi-file-document-edit</v-icon>
        </v-btn>
      </template>

      <!-- Payment Link -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              :disabled="isPaylinkDisabled"
              :dark="!isPaylinkDisabled"
              @click="openModal('sendOrderPaylink', 'payment', true)"
              fab
              color="info">
              <v-icon>mdi-cash-multiple</v-icon>
            </v-btn>
          </div>
        </template>

        <span>(Re)Send Order Payment/Offer</span>
      </v-tooltip>

      <!-- Order Confirmation -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              :disabled="!customer || !orderIsPaid"
              :dark="!(!customer || !orderIsPaid)"
              @click="openModal('sendConfirmationLink', 'order confirmation', true)"
              fab
              color="pink">
              <v-icon>mdi-email</v-icon>
            </v-btn>
          </div>
        </template>

        <span>(Re)Send Order Confirmation</span>
      </v-tooltip>

      <!-- Send Tickets -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              :disabled="!customer || !orderIsPaid"
              :dark="!(!customer || !orderIsPaid)"
              @click="openModal('sendTicket', 'tickets', false)"
              fab
              color="green">
              <v-icon>mdi-ticket-confirmation</v-icon>
            </v-btn>
          </div>
        </template>

        <span>(Re)Send Tickets</span>
      </v-tooltip>

      <!-- View/Send Receipts -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              :disabled="!customer || !orderIsPaid"
              :dark="!(!customer || !orderIsPaid)"
              fab
              color="orange darken-1"
              @click="listReceipts">
              <v-icon>mdi-receipt-text-outline</v-icon>
            </v-btn>
          </div>
        </template>

        <span>View/Send Receipts</span>
      </v-tooltip>

      <!-- Create Claim Ticket -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <v-btn @click="inquiryModal = true" fab dark color="purple" v-bind="attrs" v-on="on">
            <v-icon>mdi-cash-plus</v-icon>
          </v-btn>
        </template>

        <span>Create Claim Ticket</span>
      </v-tooltip>

      <!-- Unlock Order -->
      <v-tooltip top v-if="orderIsLockRequired">
        <template v-slot:activator="{ on, attrs }">
          <v-btn @click="updatelock('unlock')" fab dark color="indigo" v-bind="attrs" v-on="on">
            <v-icon>mdi-lock-off-outline</v-icon>
          </v-btn>
        </template>
        <span>Unlock Order</span>
      </v-tooltip>

      <!-- Debit the Order via Free -->
      <v-tooltip top v-if="!isCompany">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              @click="handleDebitDialog('free')"
              fab
              color="teal"
              :disabled="orderIsFullyPaid || orderTotalPrice == null || orderTotalPrice !== 0">
              <v-icon color="white">mdi-cash-remove</v-icon>
            </v-btn>
          </div>
        </template>
        <span>Debit the Order via Free</span>
      </v-tooltip>

      <!-- Debit the Order via Invoice -->
      <v-tooltip top v-if="isCompany">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              @click="handleDebitDialog('fortnox')"
              fab
              color="danger"
              :disabled="orderIsFullyPaid || !userCanInvoice"
              :dark="!(orderIsPaid || !userCanInvoice)">
              <v-icon color="white">mdi-cash-check</v-icon>
            </v-btn>
          </div>
        </template>
        <span>Debit the Order via Invoice </span>
      </v-tooltip>

      <!-- Anonymize Order -->
      <v-tooltip top v-if="canAnonymizeOrder">
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            @click="anonymizeOrder"
            fab
            color="blue-grey"
            v-bind="attrs"
            v-on="on"
            :disabled="orderIsAnonymized"
            :dark="!orderIsAnonymized">
            <v-icon>mdi-incognito</v-icon>
          </v-btn>
        </template>

        <span>Anonymize Order</span>
      </v-tooltip>

      <!-- Archive/Unarchive Order -->
      <v-tooltip top>
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn
              @click="archiveOrder()"
              fab
              :disabled="orderIsPaid"
              :dark="!orderIsPaid"
              color="red"
              v-bind="attrs"
              v-on="on">
              <v-icon v-if="isArchivedOrder">mdi-archive-off</v-icon>
              <v-icon v-else>mdi-archive-arrow-down</v-icon>
            </v-btn>
          </div>
        </template>
        <span>{{ isArchivedOrder ? "Unarchive order" : "Archive order" }}</span>
      </v-tooltip>
    </v-speed-dial>

    <!-- send pay / confirm link -->
    <v-dialog v-model="sendLinkModal" max-width="700px">
      <v-overlay :value="$store.state.loading" absolute color="white" :opacity="0.8">
        <v-progress-circular indeterminate size="64" color="accent"></v-progress-circular>
      </v-overlay>
      <v-container>
        <v-card :disabled="$store.state.loading">
          <v-flex class="mb-12"></v-flex>
          <base-material-card color="warning" icon="mdi-alert-circle-outline">
            <template v-slot:after-heading>
              <div class="ml-auto body-3">
                Send {{ dialogTitle }}
                <h3>Are you sure to send the {{ dialogTitle }} to the customer?</h3>
              </div>
            </template>
            <v-divider class="mt-2" />
            <v-card-text class="pb-0">
              <div
                v-if="dialogTitle === 'order confirmation' && orderContainsRailPasses"
                class="text-h6 mb-2 font-weight-bold">
                <!-- TODO "gift cards will be attached to the confirmation e-mail" message -->
                Interrail passes will be attached to the e-mail.
              </div>
              <div v-if="dialogTitle === 'tickets'">
                <p class="subtitle-1 font-weight-bold">
                  By default, tickets will be sent to each passenger's e-mail address or phone number.
                </p>
                <div class="d-flex align-start ga-4">
                  <ValidationObserver ref="ticketEmailObserver">
                    <ValidationProvider v-slot="{ errors }" name="email" rules="email">
                      <v-text-field
                        v-model="specificEmailAddressToSendTickets"
                        label="Send to a different e-mail address"
                        hint="Leave empty to send tickets to passengers' e-mail addresses/phone numbers"
                        persistent-hint
                        clearable
                        :error-messages="errors"
                        class="mr-4" />
                    </ValidationProvider>
                  </ValidationObserver>

                  <v-select
                    v-model="ticketLanguage"
                    :items="languages"
                    item-value="value"
                    persistent-hint
                    label="Select language"
                    return-object>
                  </v-select>
                </div>
              </div>
              <div v-else>
                <v-row>
                  <v-col v-if="customer" cols="12" md="8" class="">
                    Customer's E-mail: <b> {{ customer.email }}</b>
                  </v-col>

                  <v-col v-if="this.isactiveLang" cols="12" md="4" class="mb-0">
                    <v-select
                      v-model="language"
                      :items="languages"
                      item-value="value"
                      persistent-hint
                      label="Select language"
                      return-object></v-select>
                  </v-col>
                </v-row>
              </div>
            </v-card-text>
            <template v-slot:actions>
              <v-btn color="red" outlined @click="sendLinkModal = false" x-large>Not Now </v-btn>
              <v-spacer></v-spacer>
              <v-btn
                v-if="sendLinkModal && dialogMethod !== 'sendOrderPaylink'"
                @click="$emit('get-files', dialogTitle === 'tickets' ? ticketLanguage.value : language)"
                color="accent"
                x-large
                >View Tickets
              </v-btn>
              <v-btn @click="sendAction" color="success" x-large :disabled="isEmailInvalid">Send </v-btn>
            </template>
          </base-material-card>
        </v-card>
      </v-container>
    </v-dialog>

    <!-- list/send receipts modal  -->
    <v-dialog v-model="receiptListModal" max-width="1000px" :persistent="$store.state.loading" @input="clearReceipts">
      <v-card>
        <v-toolbar class="v-sheet theme--dark v-toolbar secondary">
          <v-toolbar-title> Receipts List </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            dark
            icon
            @click="
              clearReceipts();
              receiptListModal = false;
            ">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-data-table
            v-model="selectedReceipts"
            :headers="receiptHeaders"
            :items="receipts"
            item-key="receiptData.id"
            show-select>
            <template v-slot:item.receiptData.receiptNumber="{ item }">
              <div class="subtitle-2">{{ item.receiptData.receiptNumber }}</div>
            </template>
            <template v-slot:item.receiptData.created="{ item }">
              {{ item.receiptData.created | getFullLocalizedDate }}
              <small>(GMT+{{ getGmtDiff(item.receiptData.created) }})</small>
            </template>
            <template v-slot:item.receiptData.type="{ item }">
              <v-chip :color="item.receiptData.type === 'debit' ? 'primary' : 'third'" dark small>
                {{ capitalizeFirstLetter(item.receiptData.type) }}
              </v-chip>
            </template>
            <template v-slot:item.receiptData.amount="{ item }">
              {{ item.receiptData.price.plainText }}
            </template>
            <template v-slot:item.receiptData.actions="{ item }">
              <v-btn :loading="item.loading" color="accent" class="mr-2" width="100%" @click="handleViewPdf(item)">
                View PDF
                <v-icon right>mdi-file-pdf-box</v-icon>
              </v-btn>
            </template>
          </v-data-table>

          <div class="d-flex w-100" style="gap: 16px">
            <div style="flex: 1">
              <v-select
                v-model="receiptLanguage"
                :items="languages"
                style="flex: 1"
                item-value="value"
                hide-details
                outlined
                dense
                label="Select language"
                return-object></v-select>
            </div>
            <div style="flex: 1">
              <v-tooltip :disabled="!!selectedReceipts.length" color="black" top>
                <template v-slot:activator="{ on, attrs }">
                  <div v-bind="attrs" v-on="on">
                    <v-btn
                      :loading="sendReceiptEmailLoading"
                      :disabled="!selectedReceipts.length"
                      color="success"
                      height="40px"
                      block
                      @click="sendReceiptEmail">
                      SEND SELECTED RECEIPTS TO THE CUSTOMER E-MAIL
                    </v-btn>
                  </div>
                </template>
                <span>Select a receipt in order to send receipt email to the customer</span>
              </v-tooltip>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- create inquiry modal  -->
    <v-dialog v-model="inquiryModal" max-width="700px">
      <v-container>
        <v-card :disabled="$store.state.loading">
          <v-flex class="mb-12"></v-flex>
          <base-material-card color="warning" icon="mdi-alert-circle-outline">
            <template v-slot:after-heading>
              <div class="ml-auto">
                <div class="body-3 grey--text font-weight-light" v-text="'Create a claim'" />
                <h3>Create a claim</h3>
              </div>
            </template>
            <v-divider class="mt-2" />

            <v-card-text>
              <ValidationObserver ref="inquiryObserver" v-slot="{ Validate, reset }">
                <ValidationProvider v-slot="{ errors }" name="description" rules="required">
                  <v-textarea filled label="Description" v-model="inquiryDesc" :error-messages="errors" clearable />
                </ValidationProvider>
              </ValidationObserver>
            </v-card-text>
            <template v-slot:actions>
              <v-btn color="red" outlined @click="inquiryModal = false" x-large>Not Now </v-btn>
              <v-spacer></v-spacer>
              <v-btn :loading="$store.state.loading" @click="createInquiry" color="success" x-large
                >Create Claim
              </v-btn>
            </template>
          </base-material-card>
        </v-card>
      </v-container>
    </v-dialog>

    <!-- debit via invoice to company modal  -->
    <v-dialog v-model="debitOrderModal" max-width="700px">
      <div>
        <v-card :disabled="$store.state.loading" class="elevation-0">
          <v-flex class="pb-12"></v-flex>
          <base-material-card color="warning" icon="mdi-alert-circle-outline">
            <template v-slot:after-heading>
              <div class="ml-auto">
                <div style="margin-top: -20px" class="body-3 grey--text font-weight-light" v-text="'Debit the order'" />
                <h3>
                  <!-- Debit the order with invoice to
                  <span color="text--danger">{{ customer.company.name }}</span> -->
                </h3>
              </div>
            </template>

            <v-divider class="mt-2" />

            <v-card-text>
              <h2 class="mb-2">
                <!-- The company has to pay the invoice in {{ customer.company.allowedDaysToPay }} days! -->
              </h2>
              <h3>The confirmation mail and tickets WILL be sent automatically.</h3>
            </v-card-text>
            <template v-slot:actions>
              <v-btn color="red" outlined @click="handleCloseDebitDialog" x-large>Not Now </v-btn>
              <v-spacer></v-spacer>
              <v-btn :loading="$store.state.loading" @click="debitTheOrder" color="success" x-large>Debit </v-btn>
            </template>
          </base-material-card>
        </v-card>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { getErrorMessage } from "@/util/helpers";
import Trainplanet from "@/util/trainplanet.api";
import { ROLES } from "@/store/modules/roles";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "FloatButtons",
  components: {
    ValidationProvider,
    ValidationObserver,
  },
  props: {
    isArchivedOrder: {
      required: true,
    },
    isCompany: null,
    orderId: {
      required: true,
    },
    customer: {
      required: true,
      default: null,
    },
    orderIsPaid: {
      required: true,
    },
    orderIsFullyPaid: {
      required: true,
    },
    orderIsAnonymized: {
      required: true,
    },
    orderIsPaymentPossible: {
      type: Boolean,
      default: true,
    },
    orderContainsRailPasses: {
      required: true,
    },
    orderLocale: {
      default: "sv-SE",
      required: false,
    },
    orderTotalPrice: {
      type: Number,
      default: null,
    },
    orderIsLockRequired: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isArchivedOrderInternal: this.isArchivedOrder,
      debitOrderModal: false,
      fab: false,
      sendLinkModal: false,
      inquiryModal: false,
      receiptListModal: false,
      receipts: [],
      receiptLanguage: { value: this.orderLocale },
      ticketLanguage: { value: this.orderLocale },
      type: "all",
      selectedReceipts: [],
      sendReceiptEmailLoading: false,
      isactiveLang: false,
      //TODO change it to system default lang
      language: "sv-SE",
      dialogMethod: null,
      dialogTitle: "",
      inquiryDesc: "",
      languages: [],
      receiptHeaders: [
        { text: "Receipt Number", value: "receiptData.receiptNumber" },
        { text: "Type", value: "receiptData.type" },
        { text: "Created", value: "receiptData.created" },
        { text: "Amount", value: "receiptData.price.plainText" },
        { text: "", value: "receiptData.actions", sortable: false, align: "end" },
      ],
      selectedSystem: "",
      specificEmailAddressToSendTickets: "",
    };
  },
  computed: {
    ...mapGetters({
      userRoles: "auth/userRoles",
      userCanInvoice: "auth/canInvoiceOrders",
    }),
    isEmailInvalid() {
      if (this.dialogTitle !== "tickets") {
        return false;
      }
      if (this.specificEmailAddressToSendTickets && this.$refs.ticketEmailObserver) {
        return this.$refs.ticketEmailObserver.flags.invalid;
      }
      return false;
    },
    isPaylinkDisabled() {
      return (
        !this.customer ||
        this.orderIsFullyPaid ||
        this.orderIsAnonymized ||
        this.isArchivedOrder ||
        !this.orderIsPaymentPossible
      );
    },
    canAnonymizeOrder() {
      return this.userRoles.includes(ROLES.ADMIN) || this.userRoles.includes(ROLES.ORDER_BASIC);
    },
  },
  filters: {
    getFullLocalizedDate(datetime) {
      return moment.utc(datetime).local().format("YYYY-MM-DD HH:mm");
    },
  },
  methods: {
    getGmtDiff() {
      return moment().utcOffset() / 60;
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    async debitTheOrder() {
      const system = this.selectedSystem;

      if (!system) {
        await this.$store.dispatch("error", "Payment system is missing.");
        return;
      }

      try {
        await this.$store.dispatch("loading", true);
        const res = await Trainplanet.getPaymentSession(this.orderId, system);
        await Trainplanet.completePaymentSession(res.pushUrl);

        if (system === "fortnox") {
          await this.$router.push({
            name: "invoices",
          });
        }

        if (system === "free") {
          await this.$store.dispatch("success", "Order debited via free.");
        }

        this.$emit("initials");
        this.handleCloseDebitDialog();
      } catch (error) {
        await this.$store.dispatch("error", getErrorMessage(error));
      } finally {
        await this.$store.dispatch("loading", false);
      }
    },
    async sendAction() {
      try {
        await this.$store.dispatch("loading", true);
        const sendActionLanguage = this.dialogTitle === "tickets" ? this.ticketLanguage.value : this.language;
        await Trainplanet[this.dialogMethod](this.orderId, sendActionLanguage, this.specificEmailAddressToSendTickets);
        await this.$store.dispatch("success", this.dialogTitle + " has been sent.");
        this.sendLinkModal = false;
        this.specificEmailAddressToSendTickets = "";
      } catch (error) {
        let message = getErrorMessage(error);
        await this.$store.dispatch("error", message);
      } finally {
        await this.$store.dispatch("loading", false);
        this.$emit("initials");
      }
    },
    openModal(method, title, lang) {
      this.dialogTitle = title;
      this.dialogMethod = method;
      this.sendLinkModal = true;
      this.isactiveLang = lang;
    },
    async updatelock(method, title) {
      this.$confirm("Are you sure that you want to " + method + " this order?", {
        color: "primary",
        title: method + " Order",
      }).then(async (res) => {
        if (!res) return;
        try {
          await this.$store.dispatch("loading", true);
          await Trainplanet[method](this.orderId);
          await this.$store.dispatch("success", "Order has been " + method + "ed.");
        } catch (error) {
          let message = getErrorMessage(error);
          await this.$store.dispatch("error", message);
        } finally {
          await this.$store.dispatch("loading", false);
          this.$emit("initials");
        }
      });
    },
    async archiveOrder() {
      const action = this.isArchivedOrder ? "unarchive" : "archive";
      const confirmTitle = this.isArchivedOrder ? "Unarchive Order" : "Archive Order";
      const successMessage = this.isArchivedOrder ? "Order has been unarchived." : "Order has been archived.";
      const successIcon = !this.isArchivedOrder ? "mdi-archive-arrow-down" : "mdi-archive-off";
      this.$confirm(`Are you sure that you want to ${action} this order?`, {
        color: "red",
        title: confirmTitle,
        icon: successIcon,
      }).then(async (res) => {
        if (!res) return;
        try {
          await this.$store.dispatch("loading", true);
          const result = await Trainplanet.archiveOrder(this.orderId);
          this.isArchivedOrderInternal = result.newArchivedStatus;
          await this.$store.dispatch("success", successMessage);
          this.$emit("initials");
        } catch (error) {
          let message = getErrorMessage(error);
          await this.$store.dispatch("error", message);
        } finally {
          await this.$store.dispatch("loading", false);
        }
      });
    },
    createInquiry() {
      this.$refs.inquiryObserver.validate().then((res) => {
        if (!res) return;
        const input = {
          orders: [this.orderId],
          description: this.inquiryDesc,
        };
        this.$store.dispatch("loading", true);
        Trainplanet.createInquiry(input)
          .then((data) => {
            this.$router.push({
              name: "inquiries-edit",
              params: { inquiryId: data.id },
            });
          })
          .catch((error) => {
            let message = getErrorMessage(error);
            this.$store.dispatch("error", message);
          })
          .finally(() => {
            this.$store.dispatch("loading", false);
          });
      });
    },
    anonymizeOrder() {
      this.$confirm("Are you sure that you want to anonymize this order?", {
        color: "red",
        title: "Anonymize Order",
      }).then(async (res) => {
        if (!res) return;

        try {
          await this.$store.dispatch("loading", true);
          await Trainplanet.anonymizeOrder(this.orderId);
          await this.$store.dispatch("success", "Order has been successfully anonymized.");
        } catch (error) {
          await this.$store.dispatch("error", getErrorMessage(error));
        } finally {
          await this.$store.dispatch("loading", false);
          this.$emit("initials");
        }
      });
    },
    async listReceipts() {
      this.$store.dispatch("loading", true);

      let payload = {};
      if (this.type !== "all") {
        payload.type = this.type;
      }

      Trainplanet.getReceipts(this.orderId, payload, this.receiptLanguage)
        .then((res) => {
          this.receipts = res;
          this.receiptListModal = true;
        })
        .catch((error) => {
          this.$store.dispatch("error", getErrorMessage(error));
        })
        .finally(() => {
          this.$store.dispatch("loading", false);
        });
    },
    async sendReceiptEmail() {
      this.sendReceiptEmailLoading = true;

      let payload = {
        receiptIds: this.selectedReceipts.map((receipt) => receipt.receiptData.id),
      };

      if (this.type !== "all") {
        payload.type = this.type;
      }

      Trainplanet.sendReceiptEmail(this.orderId, payload, this.receiptLanguage)
        .then(() => {
          this.$store.dispatch("success", "Receipt email has been sent to the customer.");

          this.receiptListModal = false;
          this.clearReceipts();
        })
        .catch((error) => {
          this.$store.dispatch("error", getErrorMessage(error));
        })
        .finally(() => {
          this.sendReceiptEmailLoading = false;
        });
    },
    clearReceipts() {
      this.selectedReceipts = [];
    },
    handleViewPdf(item) {
      const payload = {
        receiptIds: [item.receiptData.id],
        getAsPdfLink: true,
      };

      this.$set(item, "loading", true);

      Trainplanet.getReceipts(this.orderId, payload, this.receiptLanguage)
        .then((res) => {
          window.open(res[0].receiptPdf.accessUrl, "_blank");
        })
        .catch((error) => {
          this.$store.dispatch("error", getErrorMessage(error));
        })
        .finally(() => {
          this.$set(item, "loading", false);
        });
    },
    async getLocals() {
      await Trainplanet.getOrderSystemOptions()
        .then((res) => {
          for (const [local, text] of Object.entries(res.locales)) {
            this.languages.push({
              text: text,
              value: local,
            });
          }
        })
        .catch((error) => {
          let message = getErrorMessage(error);
          this.$store.dispatch("error", message);
        });
    },
    handleDebitDialog(system) {
      if (!system) return;
      this.selectedSystem = system;
      this.debitOrderModal = true;
    },
    handleCloseDebitDialog() {
      this.selectedSystem = "";
      this.debitOrderModal = false;
    },
  },
  created() {
    this.getLocals();
  },
};
</script>

<style scoped>
.v-sheet.v-card:not(.v-sheet--outlined) {
  box-shadow: none !important;
}
</style>
